import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesLeft, faAnglesRight, faWarehouse, faClipboardList, faChartBar, faTruckFast, faClipboardCheck, faDownload, faCalendarCheck, faMap, faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useNavigate } from 'react-router-dom';

import { useTheme } from './themeControl/ThemeProvider';
import { useAlert } from '../providers/AlertProvider';
import { useAuthProvider } from '../providers/AuthProvider';
import { useSupabase } from '../providers/SupabaseProvider';

import { ActivityIndicator } from '../components/custom/ActivityIndicator';

// Import API functions
import { check_pending_orders } from '../api/notifications';

// Import CSS
import './css/GlobalStyles.css';
import './css/DriversStyles.css';
import './css/LeftNavigation.css';
import './css/Styles.css';

// Warehouse components
import EnhancedWarehouseManagement from './custom/EnhancedWarehouseManagement';
import DownloadWarehouseCharges from './custom/DownloadWarehouseCharges';
import OrdersManagement from './custom/OrdersManagement';
import InboundLoadsManagement from './custom/InboundLoadsManagement';
import StockTakeManagement from './custom/StockTakeManagement';
import WarehouseReports from './custom/WarehouseReports';
import InboundBookingsManagement from './custom/InboundBookingsManagement';
import WarehouseMap from './custom/WarehouseMap';
import WarehouseActivityLog from './custom/WarehouseActivityLog';

// Warehouse page functions
import { WarehousePageFunctions } from '../functions/warehouse_page/warehouse_page_functions';

// import images
import './img/light-driver.png';
import './img/dark-driver.png';
import './img/light-admin.png';
import './img/dark-admin.png';
import './img/light-service.png';
import './img/dark-service.png';

const WarehousePage = () => {
    const { theme } = useTheme();
    const { showAlert } = useAlert();
    const supabase = useSupabase();
    const { logout, isAdmin, isWarehouse, isCustomer, profile, isLoading: authLoading, hasCustomerWarehouseAccess, isManager, isDev, permissions, features } = useAuthProvider();

    // Management = manager or dev role (full warehouse access)
    const isManagement = isManager || isDev;
    const navigate = useNavigate();

    // Initialize warehouse page functions (with alerts)
    const warehouseFunctions = new WarehousePageFunctions(showAlert);

    // State management
    const [isLoading, setIsLoading] = useState(true);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    
    // Debug wrapper for setSelectedCustomer to track all changes
    const debugSetSelectedCustomer = (value) => {
        setSelectedCustomer(value);
    };
    const [customerName, setCustomerName] = useState(null); // Start as null, will be set based on user type
    const selectedCustomerRef = useRef(null);
    const palletRealtimeTimeoutRef = useRef(null);
    const [warehouseOptions, setWarehouseOptions] = useState('enhanced');
    const [showLeftNav, setShowLeftNav] = useState(false);
    const [warehouseLanes, setWarehouseLanes] = useState({});
    const [warehouseProducts, setWarehouseProducts] = useState([]);
    const [warehouseCustomers, setWarehouseCustomers] = useState([]);
    const [pendingOrdersCount, setPendingOrdersCount] = useState(0);

    const [allPallets, setAllPallets] = useState([]); // Store all pallets for validation and movement
    
    // Warehouse summary data for sharing between components
    const [warehouseSummaryProducts, setWarehouseSummaryProducts] = useState([]);
    const [allCustomerPallets, setAllCustomerPallets] = useState([]);

    const [showDownloadIMFData, setShowDownloadIMFData] = useState(false);

    // Access control
    const restricted = (!isAdmin && !isCustomer && !isWarehouse );
    const permRestricted = isAdmin && !isDev && !permissions.perm_warehouse_view && !permissions.perm_warehouse_edit;
    const canEdit = isDev || isWarehouse || permissions.perm_warehouse_edit;
    const canViewStockTakes = isCustomer || canEdit;
    const customerRestricted = (!isAdmin && !isWarehouse);

    // Redirect customers without warehouse access
    useEffect(() => {
        if (!authLoading && isCustomer && !hasCustomerWarehouseAccess) {
            navigate('/customer-portal');
        }
    }, [authLoading, isCustomer, hasCustomerWarehouseAccess, navigate]);

    // sort warehouse customers
    useEffect(() => {
        // Use the customers filtered by warehouse functions instead of filtering here
        // This prevents conflicts with the optimized customer filtering in warehouse_page_functions.jsx
        setWarehouseCustomers(customers || []);
    }, [customers]);

    // Keep selectedCustomerRef in sync with selectedCustomer state
    useEffect(() => {
        selectedCustomerRef.current = selectedCustomer;
    }, [selectedCustomer]);

    // Load the main warehouse data
    const loadPageData = useCallback(async (showLoading = true) => {
        
        // Don't load data if authentication is still loading
        if (authLoading) {
            return;
        }
        
        // Additional check: if isCustomer is true but profile is not loaded yet, wait
        if (isCustomer && !profile) {
            return;
        }
        
        // Additional check: if we have a profile but isCustomer doesn't match the profile type
        if (profile && profile.group === 'CUSTOMER' && !isCustomer) {

            return;
        }
        
        await warehouseFunctions.loadPageData( 
            showLoading ? setIsLoading : () => {},
            setCustomers, 
            debugSetSelectedCustomer, 
            setWarehouseLanes, 
            setWarehouseProducts, 
            setPendingOrdersCount, 
            restricted, 
            customerRestricted, 
            logout, 
            navigate, 
            profile, 
            isCustomer,
            setWarehouseSummaryProducts,
            setAllCustomerPallets,
            selectedCustomerRef.current,
            setCustomerName,
            setAllPallets
        );

        if (!showLoading && !isCustomer && selectedCustomerRef.current) {
            await warehouseFunctions.loadSelectedCustomerData(
                selectedCustomerRef.current,
                () => {},
                setWarehouseLanes,
                setWarehouseProducts,
                setAllCustomerPallets
            );
        }
    }, [authLoading, restricted, customerRestricted, logout, navigate, profile, isCustomer]);

    useEffect(() => {
        if (!supabase || authLoading) return;

        const channel = supabase
            .channel(`warehouse-pallet-items-${Date.now()}`)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'pallet_items' },
                () => {
                    if (palletRealtimeTimeoutRef.current) {
                        clearTimeout(palletRealtimeTimeoutRef.current);
                    }

                    palletRealtimeTimeoutRef.current = setTimeout(() => {
                        loadPageData(false);
                    }, 250);
                }
            )
            .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    console.info('Subscribed to pallet item realtime updates');
                } else if (status === 'CHANNEL_ERROR') {
                    console.error('Pallet item realtime subscription failed');
                }
            });

        return () => {
            if (palletRealtimeTimeoutRef.current) {
                clearTimeout(palletRealtimeTimeoutRef.current);
                palletRealtimeTimeoutRef.current = null;
            }
            supabase.removeChannel(channel);
        };
    }, [supabase, authLoading, loadPageData]);

    // Load initial data - but only after auth is loaded, with delay to let auth state stabilize
    useEffect(() => {
        if (!authLoading) {
            const timer = setTimeout(() => {
                loadPageData();
            }, 1000);
            
            return () => clearTimeout(timer);
        }
    }, [loadPageData, authLoading]);

    const handleDownloadIMFData = async (date) => {
        warehouseFunctions.downloadCharges(date);
    }

    const handleInvoiceWarehouseCharges = async (date, accountCodeId) => {
        warehouseFunctions.invoiceCharges(date, accountCodeId);
    }

    // Handle inbound load submission
    const handleInboundSubmission = async (inboundData) => {
        try {
            const result = await warehouseFunctions.submitInboundLoad(inboundData, profile.id);
            
            // Stop loading immediately after successful submission
            if (result.status === 200 || result.status === 201) {
                setIsLoading(false);
                
                // Refresh warehouse data in the background (optional - removes loading delay)
                // This runs after loading is stopped so user can continue working
                setTimeout(() => {
                    warehouseFunctions.loadPageData( 
                        () => {}, // Empty function - don't change loading state during background refresh
                        setCustomers, 
                        debugSetSelectedCustomer,
                        setWarehouseLanes, 
                        setWarehouseProducts, 
                        setPendingOrdersCount, 
                        restricted, 
                        customerRestricted, 
                        logout, 
                        navigate, 
                        profile, 
                        isCustomer,
                        setWarehouseSummaryProducts,
                        setAllCustomerPallets,
                        selectedCustomerRef.current
                    );
                }, 100);
            } else {
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false); // Stop loading on error
        }
    };

    // Get active pallets from warehouse lanes
    const getActivePallets = useMemo(() => {
        const pallets = [];
        
        // Ensure warehouseLanes is an object and not null/undefined
        if (warehouseLanes && typeof warehouseLanes === 'object' && !Array.isArray(warehouseLanes)) {
            Object.values(warehouseLanes).forEach(laneData => {
                if (laneData && laneData.pallets && Array.isArray(laneData.pallets)) {
                    laneData.pallets.forEach(pallet => {
                        pallets.push({
                            ...pallet,
                            lane_id: laneData.lane_id || 'Unknown'
                        });
                    });
                }
            });
        }
        
        return pallets;
    }, [warehouseLanes]);

    // Memoized trigger refresh function to prevent infinite loops
    const triggerRefresh = useCallback(() => {

        console.trace('🔍 Call stack for triggerRefresh:');
        loadPageData();
    }, [loadPageData]);

    if (restricted) {
        return (
            <div style={{ 
                textAlign: 'center', 
                padding: '50px',
                color: '#ff0000'
            }}>
                <h3>Access Restricted</h3>
                <p>You do not have permission to access the warehouse management system.</p>
            </div>
        );
    }

    if (permRestricted) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <h3>Permission Required</h3>
                <p>You don't have permission to view this page. Please contact your administrator.</p>
            </div>
        );
    }

    return (
        <div id={theme} className="container-fluid closed-drivers-container">
            <Row>
                {/* Left Navigation Panel */}
                <Col className={showLeftNav ? "left-nav" : "closed-nav"} style={{ height: '100vh', paddingBottom: '150px' }} onMouseEnter={() => setShowLeftNav(true)} onMouseLeave={() => setShowLeftNav(false)}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%' }}>
                        <li title="Warehouse" className={`nav-item ${warehouseOptions === 'enhanced' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: warehouseOptions === 'enhanced' ? 'rgba(25, 151, 235, 0.8)' : 'transparent', opacity: isLoading ? 0.6 : 1 }} onClick={() => !isLoading && setWarehouseOptions('enhanced')}><FontAwesomeIcon icon={faWarehouse} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Warehouse</span></li>
                        {isAdmin && (<li title="Warehouse Map" className={`nav-item ${warehouseOptions === 'map' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: warehouseOptions === 'map' ? 'rgba(25, 151, 235, 0.8)' : 'transparent', opacity: isLoading ? 0.6 : 1 }} onClick={() => !isLoading && setWarehouseOptions('map')}><FontAwesomeIcon icon={faMap} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Warehouse Map</span></li>)}
                        <li title="Orders" className={`nav-item ${warehouseOptions === 'orders' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: warehouseOptions === 'orders' ? 'rgba(25, 151, 235, 0.8)' : 'transparent', opacity: isLoading ? 0.6 : 1 }} onClick={() => !isLoading && setWarehouseOptions('orders')}><FontAwesomeIcon icon={faClipboardList} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Orders {pendingOrdersCount > 0 && !isCustomer && <span style={{ backgroundColor: 'red', color: 'white', borderRadius: '50%', width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 'bold', marginLeft: 4 }}>{pendingOrdersCount}</span>}</span></li>
                        <li title="Reports" className={`nav-item ${warehouseOptions === 'reports' ? 'active' : ''}`} style={{ cursor: (!isWarehouse && !isLoading) ? 'pointer' : 'not-allowed', background: warehouseOptions === 'reports' ? 'rgba(25, 151, 235, 0.8)' : 'transparent', opacity: (isWarehouse || isLoading) ? 0.6 : 1 }} onClick={() => (!isWarehouse && !isLoading) ? setWarehouseOptions('reports') : null}><FontAwesomeIcon icon={faChartBar} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Reports</span></li>
                        <li title="Inbound Bookings" className={`nav-item ${warehouseOptions === 'inbound-bookings' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: warehouseOptions === 'inbound-bookings' ? 'rgba(25, 151, 235, 0.8)' : 'transparent', opacity: isLoading ? 0.6 : 1 }} onClick={() => !isLoading && setWarehouseOptions('inbound-bookings')}><FontAwesomeIcon icon={faCalendarCheck} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Inbound Bookings</span></li>
                        {!isCustomer && (<li title="Inbound" className={`nav-item ${warehouseOptions === 'inbound' ? 'active' : ''}`} style={{ cursor: (canEdit && !isLoading) ? 'pointer' : 'not-allowed', background: warehouseOptions === 'inbound' ? 'rgba(25, 151, 235, 0.8)' : 'transparent', opacity: (!canEdit || isLoading) ? 0.6 : 1 }} onClick={() => (canEdit && !isLoading) ? setWarehouseOptions('inbound') : null}><FontAwesomeIcon icon={faTruckFast} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Inbound</span></li>)}
                        <li title="Stock Take" className={`nav-item ${warehouseOptions === 'stocktake' ? 'active' : ''}`} style={{ cursor: (canViewStockTakes && !isLoading) ? 'pointer' : 'not-allowed', background: warehouseOptions === 'stocktake' ? 'rgba(25, 151, 235, 0.8)' : 'transparent', opacity: (!canViewStockTakes || isLoading) ? 0.6 : 1 }} onClick={() => (canViewStockTakes && !isLoading) ? setWarehouseOptions('stocktake') : null}><FontAwesomeIcon icon={faClipboardCheck} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Stock Take</span></li>
                        {!isCustomer && (<li title="Download Charges" className="nav-item" style={{ cursor: canEdit ? 'pointer' : 'not-allowed', background: 'transparent', opacity: canEdit ? 1 : 0.6 }} onClick={() => canEdit ? setShowDownloadIMFData(true) : null}><FontAwesomeIcon icon={faDownload} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Download Charges</span></li>)}
                        {(isManager || isDev) && (
                            <li
                                title="Warehouse Activity Log"
                                className={`nav-item ${warehouseOptions === 'activity-log' ? 'active' : ''}`}
                                style={{
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    background: warehouseOptions === 'activity-log' ? 'rgba(25, 151, 235, 0.8)' : 'transparent',
                                    opacity: isLoading ? 0.6 : 1,
                                }}
                                onClick={() => !isLoading && setWarehouseOptions('activity-log')}
                            >
                                <FontAwesomeIcon icon={faClockRotateLeft} style={{ width: 16, flexShrink: 0 }} />
                                <span className="nav-label">Activity Log</span>
                            </li>
                        )}
                    </ul>
                </Col>

                {/* Right Content Panel */}
                <Col className="closed-nav-content" style={{ padding: '20px' }}>
                    {isLoading ? (
                            <div style={{ alignSelf: 'center', justifyContent: 'center', marginTop: '30vh' }}>
                                <ActivityIndicator />
                            </div>
                    ) : (showDownloadIMFData) ? (
                            <DownloadWarehouseCharges
                                theme={theme}
                                isOpen={showDownloadIMFData}
                                onClose={() => setShowDownloadIMFData(false)}
                                onCsv={(selectedDate) => {
                                    handleDownloadIMFData(selectedDate);
                                }}
                                onInvoice={(selectedDate, accountCodeId) => {
                                    handleInvoiceWarehouseCharges(selectedDate, accountCodeId);
                                }}
                                invoicingEnabled={features?.invoicing !== false && (isDev || permissions.perm_invoice_edit)}
                            />
                    ) : (
                        <>
                            {warehouseOptions === 'enhanced' ? (
                                <div style={{ textAlign: 'center' }}>
                                    <h3>Warehouse Management</h3>
                                    <EnhancedWarehouseManagement
                                        theme={theme}
                                        management={true}
                                        customers={warehouseCustomers}
                                        allCustomers={customers}
                                        warehouseLanes={warehouseLanes}
                                        warehouseProducts={warehouseProducts}
                                        selectedCustomer={selectedCustomer}
                                        customerName={customerName}
                                        triggerRefresh={triggerRefresh}
                                        onCustomerChange={(customerId) => {

                                            // Only update if we're getting a valid customer ID, or if we're explicitly clearing (from "" to null)
                                            if (customerId || (!customerId && selectedCustomer)) {
                                                debugSetSelectedCustomer(customerId);
                                                // Update customer name when customer is selected
                                                warehouseFunctions.updateCustomerName(customerId, customers, setCustomerName);
                                            } else {

                                            }
                                        }}
                                        onProductsChange={(products) => setWarehouseProducts(products)}
                                        onWarehouseSummaryChange={(products, pallets) => {
                                            setWarehouseSummaryProducts(products || []);
                                            setAllCustomerPallets(pallets || []);
                                        }}
                                        isCustomer={isCustomer}
                                        profile={profile}
                                        allPallets={allPallets}
                                    />
                                </div>
                            ) : warehouseOptions === 'map' && isAdmin ? (
                                <WarehouseMap
                                    theme={theme}
                                    customers={customers}
                                />
                            ) : warehouseOptions === 'orders' ? (
                                <div style={{ textAlign: 'center' }}>
                                    <h3>Orders Management</h3>

                                    <OrdersManagement
                                        theme={theme}
                                        customerName={customerName}
                                        customers={customers}
                                        warehouseCustomers={warehouseCustomers}
                                        warehouseLanes={warehouseLanes}
                                        warehouseProducts={warehouseProducts}
                                        selectedCustomerId={selectedCustomer} // Pass selected customer ID from warehouse management
                                        triggerRefresh={triggerRefresh}
                                        isCustomer={isCustomer}
                                        profile={profile}
                                        isAdmin={isAdmin}
                                        isWarehouse={isWarehouse}
                                        isManagement={isManagement}
                                        warehouseData={{ lanes: warehouseLanes, products: warehouseProducts }}
                                    />
                                </div>
                            ) : warehouseOptions === 'reports' ? (
                                <div>
                                    <WarehouseReports 
                                        theme={theme}
                                        customers={warehouseCustomers}
                                        selectedCustomerId={selectedCustomer} // Pass selected customer from warehouse management
                                        isCustomer={isCustomer}
                                        isAdmin={isAdmin}
                                        isWarehouse={isWarehouse}
                                        profile={profile}
                                    />
                                </div>
                            ) : warehouseOptions === 'inbound-bookings' ? (
                                <InboundBookingsManagement
                                    isCustomer={isCustomer}
                                    isStaff={isAdmin || isWarehouse || isManagement}
                                    isAdmin={isAdmin || isManagement}
                                    profile={profile}
                                    selectedCustomerId={selectedCustomer}
                                    selectedCustomerName={warehouseCustomers.find(
                                        (customer) => customer.id?.toString() === selectedCustomer?.toString()
                                    )?.company || customerName}
                                />
                            ) : warehouseOptions === 'inbound' ? (
                                <div>
                                    <h3 style={{ textAlign: 'center', marginBottom: '2rem' }}>Inbound Loads Management</h3>

                                    <InboundLoadsManagement
                                        theme={theme}
                                        warehouseCustomers={warehouseCustomers}
                                        selectedCustomerId={selectedCustomer}
                                        warehouseSummaryProducts={warehouseSummaryProducts}
                                        allCustomerPallets={allPallets}
                                        activePallets={allPallets}
                                        activeProducts={warehouseProducts}
                                        onSubmit={handleInboundSubmission}
                                        onLoadingChange={(isLoading) => setIsLoading(isLoading)}
                                        loading={isLoading}
                                        isAdmin={isAdmin}
                                    />
                                </div>
                            ) : warehouseOptions === 'stocktake' ? (
                                <div style={{ textAlign: 'center' }}>
                                    <StockTakeManagement
                                        theme={theme}
                                        customers={customers}
                                        selectedCustomerId={selectedCustomer} // Pass selected customer from warehouse management
                                        isCustomer={isCustomer}
                                        profile={profile}
                                        isAdmin={isAdmin}
                                        isWarehouse={isWarehouse}
                                    />
                                </div>
                            ) : warehouseOptions === 'activity-log' && (isManager || isDev) ? (
                                <WarehouseActivityLog theme={theme} />
                            ) : null}
                        </>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default WarehousePage;